import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { pathFor, switchLangPath } from "../lib/paths";

export default function Header() {
  const { lang, t } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const home = pathFor("home", lang);
  const otherLang = lang === "fr" ? "en" : "fr";
  const otherLangPath = switchLangPath(location.pathname, otherLang);

  const navLinks = [
    { href: `${home}#services`, label: t("nav.services"), testid: "nav-link-services" },
    { href: `${home}#approach`, label: t("nav.approach"), testid: "nav-link-approach" },
    { href: `${home}#insights`, label: t("nav.insights"), testid: "nav-link-insights" },
    { href: `${home}#faq`, label: t("nav.faq"), testid: "nav-link-faq" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-10">
        <Link
          to={home}
          data-testid="nav-logo"
          className="flex items-center gap-2 text-slate-900"
        >
          <span
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white"
            style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
          >
            <span className="text-lg font-extrabold">D</span>
          </span>
          <span
            className="text-lg font-extrabold tracking-tight"
            style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
          >
            Dervaniq
          </span>
        </Link>

        <nav
          aria-label="Primary"
          className="hidden items-center gap-8 md:flex"
          data-testid="desktop-nav"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testid}
              className="text-sm font-medium text-slate-700 hover:text-slate-900"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 md:flex">
          <Link
            to={otherLangPath}
            data-testid={`language-switcher-to-${otherLang}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-700 hover:border-slate-900 hover:text-slate-900"
            aria-label={`${t("language.switch_label")}: ${t(`language.${otherLang}`)}`}
          >
            <Globe className="h-3.5 w-3.5" aria-hidden="true" />
            <span data-testid={`language-option-${otherLang}`}>
              {t(`language.${otherLang}_short`)}
            </span>
          </Link>
          <a
            href={`${home}#newsletter`}
            data-testid="header-cta"
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            {t("nav.cta")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          className="inline-flex items-center justify-center rounded-md p-2 text-slate-800 md:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-slate-200 bg-white md:hidden"
          style={{ backgroundColor: "#FFFFFF" }}
        >
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-100"
                >
                  {link.label}
                </a>
              ))}
              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-4">
                <Link
                  to={otherLangPath}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-language-switcher-to-${otherLang}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-700 hover:border-slate-900 hover:text-slate-900"
                >
                  <Globe className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(`language.${otherLang}_short`)}
                </Link>
                <a
                  href={`${home}#newsletter`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-header-cta"
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {t("nav.cta")}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
